import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Ticket, Calendar, DollarSign, Gavel, Check, MinusCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const oddsStr = (o) => (o === null || o === undefined ? '—' : o > 0 ? `+${o}` : `${o}`);
const decimal = (american) => (american > 0 ? american / 100 + 1 : 100 / Math.abs(american) + 1);
const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' CT';
    } catch { return iso; }
};
const STATUS_STYLE = {
    won: 'bg-[#39FF14]/20 text-[#39FF14]',
    lost: 'bg-[#FF3B30]/20 text-[#FF6B6B]',
    pending: 'bg-[#FFD700]/20 text-[#FFD700]',
    cashed_out: 'bg-[#00BFFF]/20 text-[#00BFFF]',
    void: 'bg-white/15 text-[#A0AAB2]',
};
const GRADES = [
    { key: 'won', label: 'Win', icon: Check, cls: 'bg-[#39FF14]/15 text-[#39FF14] border-[#39FF14]/40' },
    { key: 'lost', label: 'Loss', icon: X, cls: 'bg-[#FF3B30]/15 text-[#FF6B6B] border-[#FF3B30]/40' },
    { key: 'void', label: 'Void', icon: MinusCircle, cls: 'bg-white/10 text-[#A0AAB2] border-white/20' },
];

/**
 * TicketDetailModal — full, screenshot-friendly view of a single sports ticket.
 * Read-only by default. Pass `adminGrade` to enable in-modal grading of a PENDING
 * ticket (win/loss/void per leg → Finalize & Settle), reusing the manual-grade
 * endpoint — nothing pays until finalize. `onSettled` fires after a successful settle.
 * Accepts a `bet` in the manual-grade/pending or ticket-detail shape.
 */
export default function TicketDetailModal({ bet, onClose, adminGrade = false, onSettled }) {
    const [draft, setDraft] = useState({});
    const [busy, setBusy] = useState(false);
    const legs = bet?.legs || [];
    const isParlay = (bet?.type || 'single') === 'parlay';
    const canGrade = adminGrade && bet?.status === 'pending';

    useEffect(() => {
        if (!bet) return;
        const seed = {};
        (bet.legs || []).forEach((l) => { seed[l.index] = l.grade && l.grade !== 'pending' ? l.grade : (l.status && l.status !== 'pending' ? l.status : null); });
        setDraft(seed);
    }, [bet]);

    if (!bet) return null;

    const setLeg = (idx, grade) => setDraft((p) => ({ ...p, [idx]: grade }));

    const preview = () => {
        const graded = legs.map((l) => draft[l.index]);
        if (graded.some((x) => !x)) return { label: 'Grade all legs to see result', result: null };
        if (graded.some((x) => x === 'lost')) return { label: 'LOSS — no payout', result: 'lost', payout: 0 };
        const wonLegs = legs.filter((l) => draft[l.index] === 'won');
        if (wonLegs.length === 0) return { label: `VOID — refund ${fmt(bet.amount)}`, result: 'void', payout: bet.amount };
        if (isParlay) {
            const dec = wonLegs.reduce((acc, l) => acc * decimal(l.odds), 1);
            return { label: `WIN — pays ${fmt(Math.round(bet.amount * dec * 100) / 100)}`, result: 'won', payout: Math.round(bet.amount * dec * 100) / 100 };
        }
        return { label: `WIN — pays ${fmt(bet.potential_payout)}`, result: 'won', payout: bet.potential_payout };
    };

    const finalize = async () => {
        if (legs.some((l) => !draft[l.index])) { toast.error('Grade every leg before finalizing'); return; }
        const pv = preview();
        if (!window.confirm(`Finalize this ticket?\n\nResult: ${pv.label}\n\nThis will settle it${pv.payout > 0 ? ` and credit ${fmt(pv.payout)}` : ' with no payout'}.`)) return;
        setBusy(true);
        try {
            const grades = legs.map((l) => ({ index: l.index, grade: draft[l.index] }));
            const { data } = await axios.post(`${API}/admin/sports/manual-grade/${bet.id}`, { grades, finalize: true }, { withCredentials: true });
            toast.success(`Settled: ${data.status.toUpperCase()}${data.payout > 0 ? ` · paid ${fmt(data.payout)}` : ''}`);
            onSettled && onSettled();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Finalize failed');
        } finally { setBusy(false); }
    };

    const saveDraft = async () => {
        setBusy(true);
        try {
            const grades = legs.map((l) => ({ index: l.index, grade: draft[l.index] || 'pending' }));
            await axios.post(`${API}/admin/sports/manual-grade/${bet.id}`, { grades, finalize: false }, { withCredentials: true });
            toast.success('Draft saved — no balance changed');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally { setBusy(false); }
    };

    const pv = preview();

    return (
        <div
            className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
            data-testid="ticket-detail-overlay"
        >
            <div
                className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl border border-[#FFD700]/30 bg-[#0b0b0b] shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="ticket-detail-modal"
            >
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 text-[#A0AAB2] hover:text-white z-10"
                    data-testid="ticket-detail-close"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="p-5 space-y-4" data-testid="ticket-detail-body">
                    {/* Brand header */}
                    <div className="flex items-center justify-between border-b border-white/10 pb-3">
                        <div className="flex items-center gap-2">
                            <Ticket className="w-5 h-5 text-[#FFD700]" />
                            <div>
                                <p className="text-sm font-black tracking-wider text-[#FFD700] uppercase">BETOHALICS</p>
                                <p className="text-[9px] uppercase tracking-widest text-[#A0AAB2]">Bet Slip</p>
                            </div>
                        </div>
                        <span className="text-[9px] uppercase px-2 py-1 rounded-full bg-white/10 text-white font-bold tracking-wide">
                            {isParlay ? `${legs.length}-Leg Parlay` : 'Single'}
                        </span>
                    </div>

                    {/* Settlement status */}
                    {bet.status && (
                        <div className="flex items-center justify-between text-xs" data-testid="ticket-detail-status">
                            <span className="text-[#A0AAB2]">Status</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLE[bet.status] || 'bg-white/10 text-[#A0AAB2]'}`}>
                                {(bet.status || '').replace('_', ' ').toUpperCase()}
                                {['won', 'void'].includes(bet.status) && bet.final_payout ? ` · ${fmt(bet.final_payout)}` : ''}
                            </span>
                        </div>
                    )}

                    {/* Player + meta */}
                    <div className="space-y-1.5 text-xs">
                        <div className="flex items-center justify-between">
                            <span className="text-[#A0AAB2]">Player</span>
                            <span className="text-white font-bold" data-testid="ticket-detail-player">{bet.name || 'Player'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-[#A0AAB2] flex items-center gap-1"><Calendar className="w-3 h-3" />Placed</span>
                            <span className="text-white font-mono">{fmtDate(bet.placed_at)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-[#A0AAB2] font-mono text-[10px]">Ticket ID</span>
                            <span className="text-[#666] font-mono text-[10px]">{bet.id}</span>
                        </div>
                    </div>

                    {/* Legs */}
                    <div className="space-y-2">
                        <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold">
                            {isParlay ? 'Selections' : 'Selection'}
                        </p>
                        {legs.map((l, i) => (
                            <div key={l.index ?? i} className="rounded-lg border border-white/10 bg-[#111] p-3" data-testid={`ticket-detail-leg-${i}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <p className="text-[13px] text-white font-bold leading-snug">
                                        {isParlay && <span className="text-[#FFD700] mr-1">{i + 1}.</span>}
                                        {l.description && !((l.outcome_name || '').toLowerCase().startsWith(l.description.toLowerCase())) ? `${l.description} ` : ''}{l.outcome_name}
                                    </p>
                                    <span className="text-[12px] font-mono font-bold text-[#39FF14] shrink-0">{oddsStr(l.odds)}</span>
                                </div>
                                <p className="text-[11px] text-[#A0AAB2] mt-1">
                                    {l.market_label}{l.point !== null && l.point !== undefined ? ` · line ${l.point}` : ''}
                                </p>
                                {(l.away_team || l.home_team) && (
                                    <p className="text-[11px] text-white/80 mt-0.5">{l.away_team} @ {l.home_team}</p>
                                )}
                                {l.commence_time && (
                                    <p className="text-[10px] text-[#666] mt-0.5">{fmtDate(l.commence_time)}</p>
                                )}
                                {canGrade && (
                                    <div className="flex gap-1 mt-2">
                                        {GRADES.map((gr) => {
                                            const active = draft[l.index] === gr.key;
                                            const Icon = gr.icon;
                                            return (
                                                <button key={gr.key} type="button"
                                                    onClick={() => setLeg(l.index, gr.key)}
                                                    className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border transition-colors ${active ? gr.cls : 'border-white/10 text-[#666] hover:text-white hover:border-white/30'}`}
                                                    data-testid={`modal-grade-${bet.id}-${l.index}-${gr.key}`}>
                                                    <Icon className="w-3 h-3" />{gr.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Totals */}
                    <div className="rounded-lg border border-[#FFD700]/25 bg-[#FFD700]/5 p-3 space-y-1.5 text-xs">
                        {isParlay && bet.combined_odds !== null && bet.combined_odds !== undefined && (
                            <div className="flex items-center justify-between">
                                <span className="text-[#A0AAB2]">Combined odds</span>
                                <span className="text-white font-mono font-bold">{oddsStr(bet.combined_odds)}</span>
                            </div>
                        )}
                        <div className="flex items-center justify-between">
                            <span className="text-[#A0AAB2] flex items-center gap-1"><DollarSign className="w-3 h-3" />Stake</span>
                            <span className="text-white font-mono font-bold">{fmt(bet.amount)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-[#A0AAB2]">To win</span>
                            <span className="text-[#39FF14] font-mono font-black">{fmt(bet.potential_payout)}</span>
                        </div>
                    </div>

                    {/* Admin grading footer */}
                    {canGrade && (
                        <div className="rounded-lg border border-[#FFD700]/30 bg-[#FFD700]/[0.03] p-3 space-y-2" data-testid="ticket-detail-grade">
                            <div className="flex items-center gap-1.5 text-[11px] font-bold text-[#FFD700]">
                                <Gavel className="w-3.5 h-3.5" /> Grade &amp; settle — nothing pays until finalize
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <span className={`text-[11px] font-bold ${pv.result === 'won' ? 'text-[#39FF14]' : pv.result === 'lost' ? 'text-[#FF6B6B]' : pv.result === 'void' ? 'text-[#A0AAB2]' : 'text-[#666]'}`} data-testid="modal-grade-preview">
                                    {pv.label}
                                </span>
                                <div className="flex gap-2">
                                    <button type="button" onClick={saveDraft} disabled={busy}
                                        className="text-[11px] px-3 py-1.5 rounded-md border border-white/15 text-[#A0AAB2] hover:text-white hover:border-white/30 disabled:opacity-50"
                                        data-testid="modal-grade-save">
                                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save draft'}
                                    </button>
                                    <button type="button" onClick={finalize} disabled={busy || pv.result === null}
                                        className="text-[11px] px-3 py-1.5 rounded-md bg-[#FFD700] text-black font-bold hover:bg-[#e6c200] disabled:opacity-40 disabled:cursor-not-allowed"
                                        data-testid="modal-grade-finalize">
                                        Finalize &amp; Settle
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <p className="text-center text-[9px] text-[#555] uppercase tracking-widest">
                        {canGrade ? 'Grade each leg, then finalize to settle' : 'Screenshot to verify · read-only'}
                    </p>
                </div>
            </div>
        </div>
    );
}
